import type { PureQueryOptions } from '@apollo/client';
import { gql } from '~/apollo/client-v3';
import { useQuery } from '@apollo/client';
import * as R from 'ramda';
import * as fragments from '~/apollo/fragments';
import type {
  OutcropCrossSectionsTabQuery,
  OutcropCrossSectionsTabQueryVariables,
} from '~/apollo/generated/v3/graphql';
import { BookmarkParentType } from '~/apollo/generated/v3/graphql';
import { NotFound } from '~/components/common/NotFound';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { SupportingObjectList } from '~/components/supportingObject/SupportingObjectList';
import { TaggedSupportingObjectList } from '~/components/supportingObject/TaggedSupportingObjectList';
import { useOutcropOutletContext } from '~/routes/outcrop/$outcropId';

const OUTCROP_CROSS_SECTIONS_TAB = gql`
  query OutcropCrossSectionsTab($outcropId: Int!) {
    outcropList(id: $outcropId) {
      ...outcropParts

      crossSections {
        ...crossSectionParts
        pictures {
          ...pictureParts
          file {
            ...fileParts
            signedUrl
          }
        }
        files {
          ...fileParts
          signedUrl
        }
        georeference {
          ...georeferenceParts
        }
      }

      studies {
        ...studyParts

        dataHistory {
          ...dataHistoryParts
        }

        crossSections {
          ...crossSectionParts
          pictures {
            ...pictureParts
            file {
              ...fileParts
              signedUrl
            }
          }
          files {
            ...fileParts
            signedUrl
          }
          georeference {
            ...georeferenceParts
          }
        }
      }
    }
  }

  ${fragments.outcropParts}
  ${fragments.crossSectionParts}
  ${fragments.pictureParts}
  ${fragments.fileParts}
  ${fragments.studyParts}
  ${fragments.dataHistoryParts}
  ${fragments.georeferenceParts}
`;

export default function OutcropCrossSectionsRoute() {
  const ctx = useOutcropOutletContext();
  const outcropId = ctx.outcrop.id;

  const { data, loading } = useQuery<
    OutcropCrossSectionsTabQuery,
    OutcropCrossSectionsTabQueryVariables
  >(OUTCROP_CROSS_SECTIONS_TAB, { variables: { outcropId } });

  const refetchQueries: PureQueryOptions[] = [
    { query: OUTCROP_CROSS_SECTIONS_TAB, variables: { outcropId } },
  ];

  const outcrop = data?.outcropList.find(oc => oc.id === outcropId);
  const crossSections = outcrop?.crossSections ?? [];
  const outcropStudies = R.sortBy(R.prop('name'), outcrop?.studies ?? []);

  if (loading) return <SpinnerPlaceholder />;
  if (!outcrop) return <NotFound />;

  return (
    <div className="space-y-4">
      <SupportingObjectList
        items={crossSections}
        soParentType="outcrop"
        soParentId={outcropId}
        bookmarkParentType={BookmarkParentType.CrossSection}
        refetchQueries={refetchQueries}
      />

      <TaggedSupportingObjectList
        outcropId={outcropId}
        studies={outcropStudies}
        supportObjectKey="crossSections"
        refetchQueries={refetchQueries}
      />
    </div>
  );
}
